import { Tenancy_Node_Type_Enum_Ps_Enum } from '@pypestream/api-services';
import { Icon } from '@pypestream/design-system';
import { FC, useState } from 'react';

import { tableStore } from '../../stores/table-store';
import { relevantRequests } from './engine-trace-match';
import {
  formatMs,
  passOf,
  replyTimings,
  type TimelinePass,
} from './engine-timeline';
import type { EngineRequest, EngineStep } from './validate-api';
import { langfuseHome, traceAiCalls, type TraceAiCall } from './trace-models';
import type { ValidateTurn } from './validate-runs';

/**
 * Under a bot reply: what the engine did to produce it. One line collapsed —
 * how many nodes, and where it ended up (waiting for the customer, or failed
 * and on which node); open, a timeline of each node in order, what the
 * action nodes ran, where it waited, and what broke, in plain words first
 * and the raw detail second.
 *
 * Node names come from the solution as it is in Pro Studio now, which can
 * differ from the published version that answered.
 */

const nodeName = (node: number): string | undefined => {
  const row = tableStore.rows.find(
    (r) =>
      Number(r.nodeNumber) === node &&
      r.nodeType !== Tenancy_Node_Type_Enum_Ps_Enum.G
  );
  return row?.nodeName?.trim() || undefined;
};

const rowFor = (node: number) =>
  tableStore.rows.find(
    (r) =>
      Number(r.nodeNumber) === node &&
      r.nodeType !== Tenancy_Node_Type_Enum_Ps_Enum.G
  );

/** `{BRAND_NAME}`-style holes in a span name, filled from the solution. */
const fillSpan = (span: string): string =>
  span.replace(/\{([A-Z][A-Z0-9_]*)\}/g, (hole, name: string) => {
    for (const r of tableStore.rows) {
      const raw = r.parameterInput;
      const data =
        typeof raw === 'string'
          ? (() => {
              try {
                return JSON.parse(raw);
              } catch {
                return null;
              }
            })()
          : raw;
      const value = data && (data as Record<string, unknown>)[name];
      if (typeof value === 'string' && value.trim()) return value.trim();
    }
    return hole;
  });

/** Which model an AI step called, how long it took, and where it's traced. */
const AiCall: FC<{ call: TraceAiCall }> = ({ call }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="c-engine-trace__ai-call">
      <button
        type="button"
        className={`c-engine-trace__model${call.decisions ? ' c-engine-trace__model--decisions' : ''}`}
        aria-expanded={open}
        data-testid="bobcat-validate-trace-model"
        onClick={() => setOpen((o) => !o)}
      >
        {call.model}
        {call.durationMs !== undefined && (
          <span>{formatMs(call.durationMs)}</span>
        )}
      </button>
      {call.fellBack && <span className="c-engine-trace__pill">Fell back</span>}
      {open && (
        <dl className="c-engine-trace__var-list">
          <dt>Model</dt>
          <dd>
            <code>{call.modelId}</code>
            {call.decisions && ' (OpenAI Decisions)'}
          </dd>
          <dt>Through</dt>
          <dd>
            {call.provider ? `${call.provider}, through ` : ''}the AI Gateway
          </dd>
          {call.span && (
            <>
              <dt>Traced as</dt>
              <dd>
                {fillSpan(call.span)} ·{' '}
                <a
                  href={langfuseHome(import.meta.env.FE_ADMIN_URL)}
                  target="_blank"
                  rel="noreferrer"
                >
                  Open Langfuse
                </a>
              </dd>
            </>
          )}
        </dl>
      )}
    </div>
  );
};

/**
 * The reply's AI at a glance: what routed it, the time spent in AI calls,
 * and that every call went through the gateway.
 */
const AiSummary: FC<{ calls: TraceAiCall[] }> = ({ calls }) => {
  if (!calls.length) return null;
  const router = calls[0];
  const fellBack = calls.some((c) => c.fellBack);
  const timed = calls.filter((c) => c.durationMs !== undefined);
  const total = timed.reduce((n, c) => n + (c.durationMs ?? 0), 0);
  return (
    <div
      className="c-engine-trace__ai"
      data-testid="bobcat-validate-trace-ai-summary"
    >
      <div>
        <small>{router.decisions ? 'Routed by' : 'First AI step'}</small>
        <b>{router.decisions ? 'OpenAI Decisions' : router.model}</b>
        <span>
          {fellBack
            ? 'Fell back to its model'
            : router.decisions
              ? router.modelId
              : (router.provider ?? '')}
        </span>
      </div>
      <div>
        <small>AI time</small>
        <b>{timed.length ? formatMs(total) : '—'}</b>
        <span>
          {calls.length} AI call{calls.length === 1 ? '' : 's'}
        </span>
      </div>
      <div>
        <small>Through</small>
        <b>AI Gateway</b>
        <span>Every call traced in Langfuse</span>
      </div>
    </div>
  );
};

const ACTION_KIND: Record<NonNullable<EngineStep['actionKind']>, string> = {
  system: 'built-in',
  library: 'built-in',
  custom: 'your code',
};

const KIND_LABEL: Record<EngineStep['kind'], string> = {
  decision: 'Decision',
  action: 'Action',
  other: 'Node',
};

/**
 * An engine error in a few plain words, for the headline; the raw line stays
 * underneath for whoever needs it.
 */
export const plainError = (message?: string): string => {
  if (!message) return 'Something went wrong in this node.';
  let m = message.match(/FileNotFoundError.*?'([^']+)'/);
  if (m) return `Missing file: ${m[1].split('/').pop()}`;
  m = message.match(/ModuleNotFoundError: No module named '([^']+)'/);
  if (m) return `Missing code module: ${m[1]}`;
  m = message.match(/KeyError: '?([^'\s]+)'?/);
  if (m) return `Missing value: ${m[1]}`;
  if (/timed? ?out|TimeoutError/i.test(message)) return 'Timed out';
  m = message.match(/^([A-Za-z_.]+(?:Error|Exception)):\s*(.*)$/);
  if (m) return m[2] ? `${m[1]}: ${m[2].slice(0, 120)}` : m[1];
  return message.slice(0, 140);
};

const NOTE_TEXT: Record<string, string> = {
  'No error handler node':
    'There’s no error handler node, so the customer got the platform’s default error message.',
};

/** Engine notes that say nothing useful about a run, so aren't shown. */
const HIDDEN_NOTES = new Set(['No end chat handler node']);

const Variables: FC<{ step: EngineStep }> = ({ step }) => {
  const [open, setOpen] = useState(false);
  const inputs = step.inputs ?? [];
  const outputs = step.outputs ?? [];
  if (!inputs.length && !outputs.length) return null;
  return (
    <div className="c-engine-trace__vars">
      <button
        type="button"
        className="c-engine-trace__link c-engine-trace__link--quiet"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        {open ? 'Hide' : 'Show'} variables ({inputs.length} in, {outputs.length}{' '}
        out)
      </button>
      {open && (
        <dl className="c-engine-trace__var-list">
          {inputs.length > 0 && (
            <>
              <dt>Reads</dt>
              <dd>
                {inputs.map((v) => (
                  <code key={v}>{v}</code>
                ))}
              </dd>
            </>
          )}
          {outputs.length > 0 && (
            <>
              <dt>Sets</dt>
              <dd>
                {outputs.map((v) => (
                  <code key={v}>{v}</code>
                ))}
              </dd>
            </>
          )}
        </dl>
      )}
    </div>
  );
};

const Step: FC<{ step: EngineStep; waiting: boolean; ai?: TraceAiCall }> = ({
  step,
  waiting,
  ai,
}) => {
  const name = nodeName(step.node);
  return (
    <li
      className={`c-engine-trace__step c-engine-trace__step--${step.kind}${step.failed ? ' is-failed' : ''}`}
    >
      <span className="c-engine-trace__dot" aria-hidden="true" />
      <div className="c-engine-trace__step-body">
        <div className="c-engine-trace__step-head">
          <span className="c-engine-trace__kind">{KIND_LABEL[step.kind]}</span>
          <b className="c-engine-trace__num">{step.node}</b>
          <span className="c-engine-trace__name" title={name}>
            {name ?? (step.action ? '' : 'Unnamed node')}
          </span>
          {step.failed && (
            <span className="c-engine-trace__pill c-engine-trace__pill--error">
              Failed
            </span>
          )}
          {waiting && (
            <span className="c-engine-trace__pill">Waits for the customer</span>
          )}
        </div>
        {step.action && (
          <div className="c-engine-trace__detail">
            Runs <code>{step.action}</code>
            {step.actionKind && ` · ${ACTION_KIND[step.actionKind]}`}
          </div>
        )}
        {ai && <AiCall call={ai} />}
        <Variables step={step} />
      </div>
    </li>
  );
};

/**
 * A request's walk and outcome, to spot the engine retrying the same thing.
 * Payload variable lists are left out: a line of one retry can be missing
 * from the logs, and that shouldn't make it look like a different walk.
 */
const signature = (r: EngineRequest) =>
  JSON.stringify([
    r.from,
    r.steps.map((s) => [s.node, s.action, s.failed]),
    r.outcome,
    r.error,
    r.stoppedAt,
    r.notes,
  ]);

/**
 * Consecutive identical requests, as one with a count — the engine retries a
 * failed action node, and three copies of the same walk say less than one
 * marked "retried 3 times".
 */
const collapseRetries = (requests: EngineRequest[]) =>
  requests.reduce<{ req: EngineRequest; times: number }[]>((out, req) => {
    const prev = out[out.length - 1];
    if (prev && signature(prev.req) === signature(req)) prev.times += 1;
    else out.push({ req, times: 1 });
    return out;
  }, []);

const summary = (requests: EngineRequest[]) => {
  const nodes = new Set(requests.flatMap((r) => r.steps.map((s) => s.node)));
  const failed = requests.find((r) => r.outcome === 'error');
  const last = requests[requests.length - 1];
  const count = `${nodes.size} node${nodes.size === 1 ? '' : 's'}`;
  if (failed) {
    const node = failed.error?.node;
    const name = node !== undefined ? nodeName(node) : undefined;
    return {
      tone: 'error' as const,
      text: `${count} · failed${node !== undefined ? ` at ${node}` : ''}${name ? ` ${name}` : ''}`,
    };
  }
  if (last?.stoppedAt !== undefined) {
    const name = nodeName(last.stoppedAt);
    return {
      tone: 'ok' as const,
      text: `${count} · waiting at ${last.stoppedAt}${name ? ` ${name}` : ''}`,
    };
  }
  return { tone: 'ok' as const, text: count };
};

const passTitle = (req: EngineRequest, i: number) =>
  req.from === undefined
    ? 'Then'
    : i === 0
      ? `Starts at node ${req.from}`
      : `Picks up at node ${req.from}`;

/** One request's steps on a time axis, the slowest one called out. */
const PassGantt: FC<{ pass: TimelinePass }> = ({ pass }) => {
  if (!pass.bars.length) return null;
  const total = Math.max(pass.totalMs, 1);
  const slowest = pass.bars.reduce((a, b) =>
    b.durationMs > a.durationMs ? b : a
  );
  return (
    <ol
      className="c-engine-gantt"
      data-testid="bobcat-validate-engine-gantt"
      aria-label={`Engine timeline, ${formatMs(pass.totalMs)}`}
    >
      {pass.bars.map((bar, i) => {
        const name = nodeName(bar.step.node);
        const label = `${bar.step.node}${name ? ` ${name}` : ''}${
          bar.step.action ? ` · ${bar.step.action}` : ''
        }`;
        return (
          <li
            // eslint-disable-next-line react/no-array-index-key
            key={i}
            className={`c-engine-gantt__row c-engine-gantt__row--${bar.step.kind}${
              bar.step.failed ? ' is-failed' : ''
            }${bar === slowest && bar.durationMs > 0 ? ' is-slowest' : ''}`}
          >
            <span className="c-engine-gantt__label" title={label}>
              {label}
            </span>
            <span className="c-engine-gantt__track">
              <i
                style={{
                  left: `${(bar.startMs / total) * 100}%`,
                  width: `max(2px, ${(bar.durationMs / total) * 100}%)`,
                }}
              />
            </span>
            <span className="c-engine-gantt__ms">
              {formatMs(bar.durationMs)}
            </span>
          </li>
        );
      })}
    </ol>
  );
};

/**
 * The whole run on one axis: per reply, what the customer waited and how much
 * of it the engine spent walking nodes, with the slowest node named. A row
 * opens that reply's step timeline.
 */
export const RunTimeline: FC<{
  transcript: ValidateTurn[];
  byReply: Record<string, EngineRequest[]>;
}> = ({ transcript, byReply }) => {
  const [openRow, setOpenRow] = useState<number | null>(null);
  const timings = replyTimings(transcript, byReply);
  if (!timings.length) return null;
  const scale = Math.max(
    1,
    ...timings.map((t) => Math.max(t.waitMs ?? 0, t.engineMs))
  );
  const replyNo = new Map(
    transcript
      .map((t, i) => [t, i] as const)
      .filter(([t]) => t.role === 'bot')
      .map(([, i], n) => [i, n + 1])
  );
  return (
    <section
      className="c-run-timeline"
      data-testid="bobcat-validate-run-timeline"
    >
      <header className="c-run-timeline__head">
        Timeline
        <span>
          <i className="c-run-timeline__key c-run-timeline__key--engine" />
          Engine
          <i className="c-run-timeline__key c-run-timeline__key--wait" />
          Customer waited
        </span>
      </header>
      <ol>
        {timings.map((t) => {
          const open = openRow === t.index;
          return (
            <li key={t.index} className="c-run-timeline__row">
              <button
                type="button"
                className="c-run-timeline__line"
                aria-expanded={open}
                onClick={() => setOpenRow(open ? null : t.index)}
              >
                <span className="c-run-timeline__name">
                  Reply {replyNo.get(t.index)}
                </span>
                <span className="c-run-timeline__track">
                  {t.waitMs !== null && (
                    <i
                      className="c-run-timeline__wait"
                      style={{ width: `${(t.waitMs / scale) * 100}%` }}
                    />
                  )}
                  <i
                    className="c-run-timeline__engine"
                    style={{
                      width: `max(2px, ${(t.engineMs / scale) * 100}%)`,
                    }}
                  />
                </span>
                <span className="c-run-timeline__ms">
                  {t.waitMs !== null ? formatMs(t.waitMs) : '—'}
                </span>
              </button>
              <div className="c-run-timeline__sub">
                Engine {formatMs(t.engineMs)}
                {t.slowest &&
                  ` · slowest: ${t.slowest.step.node}${
                    nodeName(t.slowest.step.node)
                      ? ` ${nodeName(t.slowest.step.node)}`
                      : ''
                  }${t.slowest.step.action ? ` (${t.slowest.step.action})` : ''} ${formatMs(
                    t.slowest.durationMs
                  )}`}
              </div>
              {open &&
                t.passes.map((p) => <PassGantt key={p.requestId} pass={p} />)}
            </li>
          );
        })}
      </ol>
      <p className="c-run-timeline__note">
        From the bot engine’s log times: a step lasts until the next one starts.
        The wait is measured by the test, to the reply’s first message.
      </p>
    </section>
  );
};

export const EngineTrace: FC<{ requests: EngineRequest[] }> = ({
  requests,
}) => {
  const [open, setOpen] = useState(false);
  const [raw, setRaw] = useState(false);
  const [timeline, setTimeline] = useState(false);
  const timed = requests.some((r) => r.steps.some((s) => s.at !== undefined));
  // The steps that answered this reply, not the setup walk before them; the
  // timeline keeps every walk, since the customer waited for all of it.
  const shown = relevantRequests(requests);
  const { tone, text } = summary(shown);
  const groups = collapseRetries(shown);
  const aiCalls = traceAiCalls(shown, rowFor);
  const aiByStep = new Map(aiCalls.map((c) => [c.step, c] as const));
  return (
    <div
      className={`c-engine-trace c-engine-trace--${tone}`}
      data-testid="bobcat-validate-engine-trace"
    >
      <button
        type="button"
        className="c-engine-trace__toggle"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <Icon name={open ? 'chevron-down' : 'chevron-right'} size="xsmall" />
        Trace · {text}
      </button>
      {open && timed && (
        <div
          className="c-validate-segments c-engine-trace__view"
          role="radiogroup"
          aria-label="Show"
        >
          {(
            [
              [false, 'Steps'],
              [true, 'Timeline'],
            ] as const
          ).map(([value, label]) => (
            <button
              key={label}
              type="button"
              role="radio"
              aria-checked={timeline === value}
              className={timeline === value ? 'is-active' : ''}
              data-testid={`bobcat-validate-trace-${label.toLowerCase()}`}
              onClick={() => setTimeline(value)}
            >
              {label}
            </button>
          ))}
        </div>
      )}
      {open && <AiSummary calls={aiCalls} />}
      {open && timeline && (
        <div className="c-engine-trace__body">
          {requests.map((req) => (
            <PassGantt key={req.requestId} pass={passOf(req)} />
          ))}
        </div>
      )}
      {open && !timeline && (
        <div className="c-engine-trace__body">
          {groups.map(({ req, times }, i) => (
            <section key={req.requestId} className="c-engine-trace__pass">
              {(groups.length > 1 || times > 1) && req.steps.length > 0 && (
                <header className="c-engine-trace__pass-head">
                  {passTitle(req, i)}
                  {times > 1 && (
                    <span className="c-engine-trace__pill">
                      Retried {times} times
                    </span>
                  )}
                </header>
              )}
              {req.steps.length > 0 && (
                <ol className="c-engine-trace__steps">
                  {req.steps.map((s, j) => (
                    <Step
                      // eslint-disable-next-line react/no-array-index-key
                      key={j}
                      step={s}
                      ai={aiByStep.get(s)}
                      waiting={
                        req.outcome !== 'error' &&
                        j === req.steps.length - 1 &&
                        req.stoppedAt === s.node
                      }
                    />
                  ))}
                </ol>
              )}
              {req.error && (
                <div className="c-engine-trace__error">
                  <strong>
                    {req.error.node !== undefined &&
                      `Node ${req.error.node} failed. `}
                    {plainError(req.error.message)}
                  </strong>
                  {req.error.message && (
                    <>
                      <button
                        type="button"
                        className="c-engine-trace__link"
                        onClick={() => setRaw((r) => !r)}
                      >
                        {raw ? 'Hide' : 'Show'} the full error
                      </button>
                      {raw && <code>{req.error.message}</code>}
                    </>
                  )}
                </div>
              )}
              {req.notes
                .filter((n) => !HIDDEN_NOTES.has(n))
                .map((n) => (
                  <p key={n} className="c-engine-trace__note">
                    {NOTE_TEXT[n] ?? `${n}.`}
                  </p>
                ))}
            </section>
          ))}
        </div>
      )}
    </div>
  );
};
